/**
 * Geometry and styling for the map's result markers and shapes.
 *
 * These live outside the components because two renderers consume them: the
 * live Leaflet layers (MapView/MapControls) and the offscreen canvas that
 * produces the print PDF (utils/mapPrint). Keeping one definition means the
 * PDF cannot drift from what is on screen.
 */

import { computeLatLngsExtent } from './mapExtent';

export const MARKER_BLUE = '#004258';
export const SHAPE_RED = '#F25244';

// Pin
export const PIN_SIZE = [25, 30];
export const PIN_ANCHOR = [12.5, 30];

const PIN_BODY_PATH =
  'M8.333 0C3.733 0 0 3.733 0 8.333C0 13.575 7.617 19.767 7.942 20L8.725 20C9.05 19.742 16.667 13.55 16.667 8.333C16.667 3.733 12.933 0 8.333 0Z';

export function buildPinSvg(selected) {
  const bodyFill = selected ? '#FFFFFF' : MARKER_BLUE;
  const bodyStroke = selected ? MARKER_BLUE : '#FFFFFF';
  const bodyStrokeWidth = selected ? 3 : 2;
  const dotFill = selected ? MARKER_BLUE : '#FFFFFF';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_SIZE[0]}" height="${PIN_SIZE[1]}" viewBox="0 0 16.6667 20" fill="none" style="overflow:visible"><path d="${PIN_BODY_PATH}" fill="${bodyFill}" stroke="${bodyStroke}" stroke-width="${bodyStrokeWidth}" vector-effect="non-scaling-stroke"/><circle cx="8.333" cy="8.333" r="2.917" fill="${dotFill}"/></svg>`;
}

// Hexagon shown in place of a polygon while zoomed out past the threshold
export const HEX_SIZE = [24, 22];
export const HEX_ANCHOR = [12, 11];
export const HEX_ZOOM_THRESHOLD = 14;

const HEX_POINTS = '6,1.5 18,1.5 23,11 18,20.5 6,20.5 1,11';

/**
 * Where a shape's hexagon sits: the record's own point if the API gave one,
 * otherwise the centre of the shape's extent.
 *
 * @param {{ position: [number, number]|null, shape: Array|null }} item
 * @returns {[number, number] | null}
 */
export function hexAnchorFor(item) {
  if (item.position) return item.position;

  const extent = computeLatLngsExtent(item.shape);
  if (!extent) return null;

  const [[latMin, lngMin], [latMax, lngMax]] = extent;
  return [(latMin + latMax) / 2, (lngMin + lngMax) / 2];
}

export function buildHexSvg(selected) {
  const fill = selected ? '#FFFFFF' : SHAPE_RED;
  const stroke = selected ? SHAPE_RED : '#FFFFFF';

  return `<svg width="${HEX_SIZE[0]}" height="${HEX_SIZE[1]}" viewBox="0 0 24 22" xmlns="http://www.w3.org/2000/svg"><polygon points="${HEX_POINTS}" fill="${fill}" stroke="${stroke}" stroke-width="2" stroke-linejoin="round" /></svg>`;
}

// Result shapes
export const POLYGON_DEFAULT_STYLE = {
  color: SHAPE_RED,
  weight: 3,
  fillColor: SHAPE_RED,
  fillOpacity: 0.2,
};

export const POLYGON_FOCUS_STYLE = {
  color: '#1A1A1A',
  weight: 3,
  fillColor: SHAPE_RED,
  fillOpacity: 0.2,
};

export const POLYGON_FOCUS_CASING_STYLE = {
  color: '#FFDD00',
  weight: 9,
  opacity: 1,
  fill: false,
};

export const POLYGON_CASING_HIDDEN_STYLE = {
  weight: 0,
  opacity: 0,
  fill: false,
};

// Boundary of the searched location
export const LOCATION_OUTLINE_STYLE = {
  color: '#117B53',
  weight: 3,
  fill: false,
};

// Geotile cluster badges, mirroring the .cluster-badge rules in MapView
export const CLUSTER_BADGE_STYLE = {
  small: { fill: '#F8BB25', size: 30 },
  medium: { fill: '#FACC5C', size: 40 },
  large: { fill: '#F57D73', size: 50 },
};

export const CLUSTER_GRID_COLOR = '#F8BB25';

export function clusterBadgeStyle(count) {
  if (count >= 1000) return CLUSTER_BADGE_STYLE.large;
  if (count >= 100) return CLUSTER_BADGE_STYLE.medium;
  return CLUSTER_BADGE_STYLE.small;
}
